import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { HTTPError, post } from "common/helpers/HTTP";

export interface FileUploadProps {
  accountId: string;
}

async function uploadFileRequest(
  workspaceId: string,
  accountId: string,
  file: Blob,
  filename: string,
  abortSignal?: AbortSignal,
): Promise<{
  file_identifier: string;
  file_input: {
    originalname: string;
    encoding: string;
    mimetype: string;
    size: number;
  };
}> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("file_name", filename);

  return await post(
    `workspaces/${workspaceId}/accounts/${accountId}/conversations/upload-file`,
    formData,
    abortSignal,
  );
}

export default function useFileUpload({ accountId }: FileUploadProps) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { mutateAsync: uploadFile, isPending: isUploading } = useMutation({
    mutationFn: ({
      file,
      filename,
      abortSignal,
    }: {
      file: Blob;
      filename: string;
      abortSignal?: AbortSignal;
    }) =>
      uploadFileRequest(workspaceId, accountId, file, filename, abortSignal),
    onError: (error) => {
      if (error instanceof HTTPError && error.code === 415) {
        toast.error(
          "Failed to upload file, unsupported file type. Please try again.",
        );
      }
    },
  });

  return {
    uploadFile,
    isUploading,
  };
}
